import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import WidgetCard from './WidgetCard';
import { useWidgetData } from './useWidgetData';

interface AttentionDoc {
  id: number;
  statusName: string;
  typeName: string;
  sender: string;
  recipient: string;
  viaWarehouse: string | null;
  idleDays: number;
  /** Сколько дней осталось до автоматической отмены */
  daysLeft: number;
}

interface AttentionResponse {
  documents: AttentionDoc[];
  limit: number;
}

const ACCENT = '#ea580c';

const plural = (n: number, one: string, few: string, many: string) => {
  const d = n % 10;
  const dd = n % 100;
  if (dd >= 11 && dd <= 19) return many;
  if (d === 1) return one;
  if (d >= 2 && d <= 4) return few;
  return many;
};

const days = (n: number) => `${n} ${plural(n, 'день', 'дня', 'дней')}`;

/**
 * Виджет «Документы, требующие внимания» - зависшие документы, которые вот-вот будут автоматически отменены
 * (товар вернется на остаток отправителю). Право: home.documents.attention.view (+ .limit)
 */
const AttentionDocumentsWidget: React.FC = () => {
  const { data, loading, error, reload } = useWidgetData<AttentionResponse>('/api/v2/home/documents/attention');
  const docs = data?.documents || [];

  return (
    <WidgetCard
      title="Документы, требующие внимания"
      icon={<WarningAmberRoundedIcon />}
      accent={ACCENT}
      loading={loading}
      error={error}
      onRefresh={reload}
      headerExtra={
        docs.length > 0 ? (
          <Chip size="small" label={docs.length} sx={{ height: 22, bgcolor: '#fff7ed', color: '#c2410c', fontWeight: 700 }} />
        ) : undefined
      }
    >
      {!loading && docs.length === 0 ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: 2, color: '#64748b' }}>
          <TaskAltRoundedIcon sx={{ color: '#10b981' }} />
          <Typography sx={{ fontSize: 13 }}>Все в порядке - зависших документов нет</Typography>
        </Box>
      ) : (
        docs.map((d, i) => {
          const urgent = d.daysLeft <= 1;
          return (
            <Box
              key={d.id}
              sx={{
                px: 2, py: 1.1, display: 'flex', alignItems: 'center', gap: 1.5,
                borderTop: i ? '1px solid #f1f5f9' : 'none', '&:hover': { bgcolor: '#f8fafc' }
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap' }}>№ {d.id}</Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 500, color: ACCENT, whiteSpace: 'nowrap' }}>{d.statusName}</Typography>
                  <Typography sx={{ fontSize: 11.5, color: '#94a3b8', whiteSpace: 'nowrap' }}>без движения {days(d.idleDays)}</Typography>
                </Box>
                <Typography sx={{ fontSize: 12.5, color: '#334155', mt: 0.25, lineHeight: 1.4 }} noWrap>
                  {d.sender || '-'} → {d.recipient || '-'}
                  {d.viaWarehouse && <Typography component="span" sx={{ fontSize: 12, color: '#94a3b8' }}> (через {d.viaWarehouse})</Typography>}
                </Typography>
              </Box>
              <Chip
                size="small"
                label={d.daysLeft === 0 ? 'Отмена сегодня' : `Отмена через ${days(d.daysLeft)}`}
                sx={{
                  height: 22, fontWeight: 600, flexShrink: 0,
                  bgcolor: urgent ? '#fef2f2' : '#fff7ed', color: urgent ? '#b91c1c' : '#c2410c'
                }}
              />
            </Box>
          );
        })
      )}
    </WidgetCard>
  );
};

export default AttentionDocumentsWidget;
